import { useQuery } from '@apollo/client';

import { channelWebhookSummaries } from 'graphql-queries';

type ChannelWebhookSummary = {
  _id: string;
  name: string;
  url: string;
  status: string;
};

/**
 * Fetches a flat list of webhook summaries for a channel, used to derive
 * duplicate-URL warnings on the WebhookCreate and WebhookEdit forms.
 *
 * Returns `[]` while the query is loading, when the query errors, when
 * `channelId` is falsy, or when the channel has no webhooks. The server is
 * the source of truth for duplicate-URL enforcement; this hook only powers
 * the client-side Inactive-duplicate warning.
 */
export function useChannelWebhookSummaries(
  channelId: string | null | undefined
): ChannelWebhookSummary[] {
  const { data } = useQuery(channelWebhookSummaries, {
    skip: !channelId,
    variables: { channelId: channelId ?? '' },
  });

  const items = data?.webhooksOnChannel?.items;

  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map(item => {
      const webhook = item?.webhook;

      if (!webhook) {
        return null;
      }

      return {
        _id: webhook._id,
        name: webhook.name,
        url: webhook.url,
        status: webhook.status,
      };
    })
    .filter((summary): summary is ChannelWebhookSummary => summary !== null);
}
